import {Component, inject, OnInit, input, output} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {RichTextEditorComponent} from '../../../../../../../shared/components/rich-text-editor/rich-text-editor.component';
import {
  ProjectCharterRolesAndStakeholdersResponse,
  ProjectCharterRolesAndStakeholdersUpsertRequest,
} from '@frontend/shared-api';
import {Step12ProjectCharterService} from '../step-1-2-project-charter.service';
import {
  ProjectStepRichTextService,
  RichTextSectionConfig,
} from '../../../../../../../shared/services/project-step-rich-text.service';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';
import {ProjectBaseStepComponent} from '../../../../project-base-step/project-base-step.component';
import {RichTextSection} from '../../../../../../../shared/model/rich-text-editor.model';
import {SECTION_KEYS} from '../../../../../../../shared/models/charter.types';

type RolesAndStakeholdersField = keyof ProjectCharterRolesAndStakeholdersUpsertRequest;

@Component({
  selector: 'app-step-1-2-project-charter-roles-and-stakeholders',
  providers: [ProjectStepRichTextService],
  imports: [ReactiveFormsModule, TranslocoPipe, RichTextEditorComponent],
  templateUrl: './step-1-2-project-charter-roles-and-stakeholders.component.html',
})
export class Step12ProjectCharterRolesAndStakeholdersComponent
  extends ProjectBaseStepComponent
  implements OnInit
{
  private readonly chartService = inject(Step12ProjectCharterService);
  readonly richTextService = inject(
    ProjectStepRichTextService<
      ProjectCharterRolesAndStakeholdersUpsertRequest,
      ProjectCharterRolesAndStakeholdersResponse
    >,
  );

  readonly preloadedData = input<ProjectCharterRolesAndStakeholdersResponse>();
  readonly statusChange = output<TabStatus>();

  onStatusChange = () => {
    this.emitStatusChange();
  };

  get rolesAndStakeholdersSections() {
    return this.richTextService.getSections();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeRichTextService();
  }

  private initializeRichTextService(): void {
    const config: RichTextSectionConfig<
      ProjectCharterRolesAndStakeholdersUpsertRequest,
      ProjectCharterRolesAndStakeholdersResponse
    > = {
      sections: ROLES_AND_STAKEHOLDERS_SECTIONS,
      fieldMap: ROLES_AND_STAKEHOLDERS_FIELD_MAP,
      saveFunction: (projectId, payload) => this.saveWithCompleteDto(projectId, payload),
      loadFunction: (projectId) => this.chartService.getCharterRolesAndStakeholders(projectId),
    };

    this.richTextService.initialize(this.projectId, config, this.preloadedData());
  }

  private saveWithCompleteDto(
    projectId: number,
    richTextPayload: ProjectCharterRolesAndStakeholdersUpsertRequest,
  ): Observable<ProjectCharterRolesAndStakeholdersResponse> {
    return this.chartService.getCharterRolesAndStakeholders(projectId).pipe(
      switchMap((existingData) => {
        const completePayload: ProjectCharterRolesAndStakeholdersUpsertRequest = {
          ...existingData,
          ...richTextPayload,
        };
        return this.chartService.saveCharterRolesAndStakeholders(projectId, completePayload);
      }),
    );
  }

  protected emitStatusChange(): void {
    const totalSections = this.richTextService.getTotalSectionsCount();
    const completedSections = this.richTextService.getCompletedSectionsCount();
    this.statusChange.emit({totalSections, completedSections});
  }
}

export const ROLES_AND_STAKEHOLDERS_SECTIONS: RichTextSection[] = [
  {
    key: SECTION_KEYS.PROJECT_ROLES_AND_RESPONSIBILITIES,
    content: '',
    tooltipKey: 'project.steps.projectChart.projectRolesAndResponsibilities',
    hasChanges: false,
  },
  {
    key: SECTION_KEYS.KEY_STAKEHOLDERS,
    content: '',
    tooltipKey: 'project.steps.projectChart.keyStakeholders',
    hasChanges: false,
  },
];

export const ROLES_AND_STAKEHOLDERS_FIELD_MAP: Record<string, RolesAndStakeholdersField> = {
  [SECTION_KEYS.PROJECT_ROLES_AND_RESPONSIBILITIES]: 'projectRolesAndResponsibilities',
  [SECTION_KEYS.KEY_STAKEHOLDERS]: 'keyStakeholders',
};
